import axios from "axios";
import { getCookie } from "@util/cookie";
import type { FlowDocumentsResult } from "@type/flow-documents";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/**
 * Thrown when the HR isn't allowed to read this application's documents (403 — a
 * different company's HR, or a non-organization role). Callers show an
 * access-denied state instead of any document data.
 */
export class FlowDocumentsForbiddenError extends Error {
    constructor(message = "You do not have access to this candidate's documents.") {
        super(message);
        this.name = "FlowDocumentsForbiddenError";
    }
}

/**
 * Fetch a candidate's Document Collection checklist for their chat-native flow.
 *
 * Contract (BE ourteam-backend#3788 — HELD until released to staging):
 *   GET /jobs/applications/{application_id}/flow/documents
 *   → StandardResponse; `data` = { step_id, step_label, deadline{state,due},
 *     items[] } where each item carries received/pending status, source,
 *     filename, content_type, download_url and (CV item) cv_parse_state.
 *
 * Auth: HR/admin of the application's company only; a 403 surfaces as
 * FlowDocumentsForbiddenError. Returns null when the application has no
 * Document Collection step (404 / empty envelope) so callers can render an
 * empty state rather than an error.
 */
export async function getApplicationFlowDocuments(
    applicationId: string
): Promise<FlowDocumentsResult | null> {
    const token = getCookie("authToken");
    try {
        const res = await axios.get(
            `${apiURL}/jobs/applications/${applicationId}/flow/documents`,
            {
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            }
        );
        const body = res.data as {
            code?: number;
            message?: string;
            data?: FlowDocumentsResult | null;
        };
        if (body?.code === 403) {
            throw new FlowDocumentsForbiddenError(body.message);
        }
        return body?.data ?? null;
    } catch (err) {
        if (err instanceof FlowDocumentsForbiddenError) throw err;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
            const msg = (err as { response?: { data?: { message?: string } } })
                ?.response?.data?.message;
            throw new FlowDocumentsForbiddenError(msg);
        }
        // No Document Collection step for this application → not an error state.
        if (status === 404) return null;
        throw err;
    }
}

/**
 * Fetch a collected document's file and return an object URL to view/download.
 * The BE `download_url` is an auth-gated route relative to the API base (e.g.
 * `/jobs/applications/{id}/flow/documents/resume/download`), so we fetch it as a
 * blob WITH the Bearer token (a plain <a href> navigation can't send the header).
 * An absolute URL (e.g. a signed S3 link) is fetched as-is without auth.
 */
export async function fetchFlowDocumentObjectUrl(downloadUrl: string): Promise<string> {
    const isAbsolute = /^https?:\/\//i.test(downloadUrl);
    const token = getCookie("authToken");
    const res = await axios.get(isAbsolute ? downloadUrl : `${apiURL}${downloadUrl}`, {
        responseType: "blob",
        headers: isAbsolute
            ? undefined
            : { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    return URL.createObjectURL(res.data as Blob);
}
